import { createContext, ReactNode, useEffect, useState } from "react";
import { useDependencies } from "../hooks/useDependencies";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBrowserLocalStorage } from "../hooks/useBrowserLocalStorage";
import { NotFoundErrorScreen } from "../routes/NotFoundErrorScreen";
import { LoadingScreen } from "../components/loading/LoadingScreen";
import { useDocumentationAreaIdAndActorId } from "../hooks/useDocumentationAreaIdAndActorId";
import { useAuth } from "src/apps/hooks/useAuth";

export type AuthenticatedLocaleContextType = {
  setLocale: (locale: string) => void;
  availableLocales: string[] | null;
  locale: string | null;
};

export const AuthenticatedLocaleContext = createContext<AuthenticatedLocaleContextType>(
  {} as AuthenticatedLocaleContextType,
);

export const AuthenticatedLocaleProvider = ({ children }: { children: ReactNode }) => {
  const { translationController } = useDependencies();
  const queryClient = useQueryClient();
  const { documentationAreaId, actorId, isDocumentationAreaIdLoading } =
    useDocumentationAreaIdAndActorId();
  const { token } = useAuth();

  const [storedLocale, setStoredLocale] = useBrowserLocalStorage<string | null>(
    `stored_query_param.locale.${documentationAreaId}`,
    null,
  );

  const [locale, setLocale] = useState<string | null>(storedLocale);

  const queryFn = async () => {
    if (!documentationAreaId) {
      throw new Error("documentationAreaId can't be undefined.");
    }

    if (!actorId) {
      throw new Error("actorId can't be undefined.");
    }

    const { availableLocales, resolvedLocale } =
      await translationController.getLocaleConfigByDocumentationAreaId(
        documentationAreaId,
        actorId,
        token,
      );
    return { availableLocales, resolvedLocale };
  };

  const { data, isLoading } = useQuery({
    queryKey: ["locale-config", documentationAreaId, actorId],
    queryFn,
    enabled: Boolean(documentationAreaId && actorId),
  });

  useEffect(() => {
    if (!locale && data?.resolvedLocale) {
      setLocale(data.resolvedLocale);
    }
  }, [data?.resolvedLocale]);

  useEffect(() => {
    setStoredLocale(locale);
    queryClient.invalidateQueries({ queryKey: ["translations"] });
  }, [locale]);

  const value = {
    locale: locale,
    availableLocales: data?.availableLocales ?? null,
    setLocale,
  };

  if (isDocumentationAreaIdLoading || isLoading) return <LoadingScreen />;
  if (!locale) return <NotFoundErrorScreen />;
  return (
    <AuthenticatedLocaleContext.Provider value={value}>
      {children}
    </AuthenticatedLocaleContext.Provider>
  );
};
